import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { UserSelector } from '@/components/shared/UserSelector';
import { EntityLinkSelector, STANDARD_LINKABLE_TYPES } from '@/components/shared/EntityLinkSelector';
import { useLinkStore } from '@/stores/linkStore';
import { useEntityNameResolver } from '@/hooks/useEntityNameResolver';
import type { Evidence } from '@/hooks/queries/useEvidence';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

const ALLOWED_LINK_TYPES = STANDARD_LINKABLE_TYPES.filter(t => t !== 'evidence');

interface EvidenceEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: any) => void;
  evidence: Evidence;
}

export function EvidenceEditDialog({ open, onOpenChange, onSave, evidence }: EvidenceEditDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
    type: 'document',
    notes: '',
    owner_id: undefined as string | undefined,
  });
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);
  const { loadLinks, links, bulkCreateLinks } = useLinkStore();
  const { resolveEntityName } = useEntityNameResolver();

  // Pre-populate form when evidence changes
  useEffect(() => {
    if (evidence && open) {
      setFormData({
        name: evidence.name || '',
        type: evidence.type || 'document',
        notes: evidence.notes || '',
        owner_id: evidence.owner_id || undefined,
      });

      // Load existing links
      loadLinks('evidence', evidence.id).then(() => {
        const evidenceLinks = links.filter(l => 
          (l.source_type === 'evidence' && l.source_id === evidence.id) ||
          (l.target_type === 'evidence' && l.target_id === evidence.id)
        );
        
        const existingLinks: SelectedEntity[] = evidenceLinks.map(l => {
          const isSource = l.source_type === 'evidence';
          const type = (isSource ? l.target_type : l.source_type) as EntityType;
          const id = isSource ? l.target_id : l.source_id;
          return {
            id,
            type,
            name: resolveEntityName(type, id),
          };
        });
        setSelectedLinks(existingLinks);
      });
    }
  }, [evidence, open, loadLinks, links, resolveEntityName]);

  const handleSubmit = async () => {
    // Handle link updates
    if (selectedLinks.length > 0 && evidence?.id) {
      const linksByType = selectedLinks.reduce((acc, link) => {
        if (!acc[link.type]) acc[link.type] = [];
        acc[link.type].push(link.id);
        return acc;
      }, {} as Record<EntityType, string[]>);
      
      for (const [targetType, targetIds] of Object.entries(linksByType)) {
        await bulkCreateLinks('evidence', evidence.id, targetType as EntityType, targetIds);
      }
    }

    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Evidence</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Evidence name"
            />
          </div>
          <div>
            <Label htmlFor="type">Type</Label>
            <Input
              id="type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              placeholder="Document type"
            />
          </div>
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes"
            />
          </div>

          <UserSelector
            value={formData.owner_id}
            onChange={(owner_id) => setFormData({ ...formData, owner_id })}
            label="Owner"
            placeholder="Select owner (optional)"
            allowUnassigned
          />

          <EntityLinkSelector
            allowedTypes={ALLOWED_LINK_TYPES}
            onLinksChange={setSelectedLinks}
            initialLinks={selectedLinks}
          />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!formData.name}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
